const moneyFormatter = new Intl.NumberFormat(Shopify.locale, { style: 'currency', currency: Shopify.currency.active });

// CART_BAR_ITEM_QUANTITY reuses the CART_BAR_ITEM_PRODUCT gift add/remove flow,
// just qualified by item count instead of cart total. A quantity rule with no
// gift_variant configured (e.g. a "buy 3, cheapest free" milestone mirroring a
// native Shopify Buy X Get Y discount) shows its own ✓ state and adds nothing
// to the cart — Shopify's discount grants the actual reward independently.
const GIFT_RULE_TYPES = ['CART_BAR_ITEM_PRODUCT', 'CART_BAR_ITEM_QUANTITY'];

class CartRule {
	constructor({
		ruleType,
		ruleID,
		startTime,
		endTime,
		giftVariantID,
		productTitle,
		shippingPrice,
		remainingText,
		threshold,
		eligibleProductIDs,
	}) {
		this.ruleType = ruleType;
		this.ruleID = ruleID;
		this.startTime = startTime || null;
		this.endTime = endTime || null;
		this.isActive = this.computeIsActive();
		this.giftVariantID = giftVariantID;
		this.productTitle = productTitle;
		this.shippingPrice = Math.round(parseFloat(shippingPrice) * 100 || 0);
		this.remainingText = remainingText;
		// when set, only cart items whose product is in this list count
		this.eligibleProductIDs = eligibleProductIDs?.length ? eligibleProductIDs : null;
		this.threshold =
			this.ruleType === 'CART_BAR_ITEM_QUANTITY'
				? parseInt(threshold, 10) || 0
				: Math.round(parseFloat(threshold) * 100 || 0);
	}
	// check if the rule is active based on the current time and the start/end times of the rule
	computeIsActive() {
		const now = Date.now();
		if (this.startTime && now < this.startTime) return false;
		if (this.endTime && now > this.endTime) return false;
		return true;
	}
	isRuleQualified({ cartTotal, discountedPricePerOrder, itemCount }) {
		if (!this.isActive) return false;
		if (this.ruleType === 'CART_BAR_ITEM_QUANTITY') {
			return !!this.threshold && itemCount >= this.threshold;
		}
		if (!cartTotal || !this.threshold) return false;
		if (this.ruleType === 'CART_BAR_ITEM_DISCOUNT') {
			return cartTotal + discountedPricePerOrder >= this.threshold;
		}
		return cartTotal >= this.threshold;
	}
	getShippingPrice({ cartTotal }) {
		if (this.ruleType !== 'CART_BAR_ITEM_SHIPPING' || !this.shippingPrice) return null;
		return this.isRuleQualified({ cartTotal }) ? 0 : this.shippingPrice;
	}
	getGiftVariantID() {
		if (!GIFT_RULE_TYPES.includes(this.ruleType) || !this.giftVariantID) return null;
		return this.giftVariantID;
	}
	isRuleGiftRemoved(removedItemIds) {
		if (!GIFT_RULE_TYPES.includes(this.ruleType) || !this.giftVariantID || !removedItemIds) return false;
		return removedItemIds.includes(this.giftVariantID);
	}
	getUpdatePayload({ cartTotal, itemCount, lineItems }) {
		if (!GIFT_RULE_TYPES.includes(this.ruleType) || !this.giftVariantID) return null;
		const giftIsInCart = lineItems.some((item) => item.variant_id === this.giftVariantID);
		const isQualified = this.isRuleQualified({ cartTotal, itemCount });
		if (giftIsInCart && !isQualified) {
			return { id: this.giftVariantID, quantity: 0 };
		}
		if (!giftIsInCart && isQualified) {
			return { id: this.giftVariantID, quantity: 1 };
		}
		return null; // No update needed
	}
	getRemainingPrice({ cartTotal }) {
		if (!this.threshold) return 0;
		const remaining = this.threshold - cartTotal;
		return remaining > 0 ? remaining : 0;
	}
	getRemainingCount({ itemCount }) {
		if (!this.threshold) return 0;
		const remaining = this.threshold - (itemCount || 0);
		return remaining > 0 ? remaining : 0;
	}
	getFormattedRemainingText({ cartTotal, itemCount }) {
		if (!this.remainingText) return '';
		if (this.ruleType === 'CART_BAR_ITEM_QUANTITY') {
			const remainingCount = this.getRemainingCount({ itemCount });
			return this.remainingText
				.replace('{product_title}', this.productTitle || '')
				.replace('{remaining_count}', remainingCount);
		}
		const remainingPrice = this.getRemainingPrice({ cartTotal });
		return this.remainingText
			.replace('{product_title}', this.productTitle || '')
			.replace('{remaining_price}', moneyFormatter.format(remainingPrice / 100).replace(/\$/g, '$$$$'));
	}
}

export { CartRule };
